import React, { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Bell, MessageCircle, BarChart3, User, Plus, LogOut, Sparkles, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';
import SparkLogo from './SparkLogo';
import PlanBadge from './PlanBadge';
import useAdminUnlock from '../hooks/useAdminUnlock';

const AppShell = () => {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const onLogoTap = useAdminUnlock();

  useEffect(() => {
    if (!user) return;
    let mounted = true;

    const fetchUnread = async () => {
      const { count } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('read', false);
      if (mounted) setUnread(count || 0);
    };
    fetchUnread();

    const channel = supabase
      .channel(`notif-badge-${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, fetchUnread)
      .subscribe();

    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (e) { /* noop */ }
    // Force full navigation to ensure ALL state (AuthContext, ProtectedRoute, subscriptions,
    // realtime channels) is fully torn down and user cannot linger on /app/* pages.
    window.location.replace('/');
  };

  const isEntrepreneur = profile?.role === 'entrepreneur';

  const navItems = [
    { to: '/app/feed', icon: Home, label: 'Feed' },
    { to: '/app/sparks', icon: Sparkles, label: 'Sparks' },
    { to: '/app/messages', icon: MessageCircle, label: 'Chat' },
    { to: '/app/notifications', icon: Bell, label: 'Avisos', badge: unread },
    { to: '/app/dashboard', icon: BarChart3, label: 'Painel' },
  ];

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#050816]/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <NavLink to="/app/feed" className="flex items-center gap-2 cursor-pointer select-none" data-testid="appshell-logo" onClick={onLogoTap}>
            <SparkLogo height={44} variant="symbol" />
            <span className="hidden sm:block text-sm font-bold tracking-wide bg-gradient-to-r from-white to-slate-300 bg-clip-text text-transparent">GetSpark</span>
          </NavLink>
          <div className="flex items-center gap-2">
            {isEntrepreneur && (
              <NavLink
                to="/app/business/new"
                className="hidden sm:inline-flex items-center gap-1.5 btn-gold rounded-xl px-4 py-2 text-xs font-bold"
              >
                <Plus size={14} /> Nova ideia
              </NavLink>
            )}
            <NavLink
              to="/app/plans"
              data-testid="appshell-plans-link"
              className="hidden sm:inline-flex items-center gap-1.5 glass-card rounded-xl px-3 py-2 text-xs font-semibold text-slate-300 hover:text-white transition-colors"
              title="Planos Premium"
            >
              <Crown size={13} className="text-[#ffd54f]" /> Premium
            </NavLink>
            {profile?.is_pro && !profile?.is_elite && <PlanBadge type="pro" size="sm" />}
            {profile?.is_elite && <PlanBadge type="elite" size="sm" />}
            <NavLink to="/app/profile" className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-white/10 bg-gradient-to-br from-[#4facfe] to-[#ffa726] flex items-center justify-center text-sm font-bold">
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                (profile?.full_name || profile?.email || 'U').charAt(0).toUpperCase()
              )}
            </NavLink>
            <button onClick={handleSignOut} className="w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-slate-400 hover:text-white transition-colors" title="Sair">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="pb-24 sm:pb-12">
        <Outlet />
      </main>

      {/* Bottom mobile nav */}
      <nav className="fixed bottom-3 left-3 right-3 sm:hidden z-50">
        <div className="glass-card rounded-full px-2 py-1.5 flex items-center justify-around">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `relative flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-full transition-all ${
                  isActive ? 'bg-gradient-to-br from-[#4facfe]/30 to-[#ffa726]/20 text-white' : 'text-slate-400'
                }`
              }
            >
              <item.icon size={18} />
              <span className="text-[9px] font-semibold">{item.label}</span>
              {item.badge > 0 && (
                <span className="absolute -top-0.5 right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-red-500 text-[8px] font-bold flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </nav>

      {/* Desktop side nav */}
      <nav className="hidden sm:flex fixed left-4 top-1/2 -translate-y-1/2 flex-col gap-2 z-40">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `relative w-12 h-12 rounded-2xl flex items-center justify-center transition-all group ${
                isActive
                  ? 'bg-gradient-to-br from-[#4facfe]/30 to-[#ffa726]/20 text-white ring-1 ring-white/15'
                  : 'glass-card text-slate-400 hover:text-white'
              }`
            }
            title={item.label}
          >
            <item.icon size={18} />
            {item.badge > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-[10px] font-bold flex items-center justify-center">
                {item.badge}
              </span>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default AppShell;
